import { Form, Head, Link } from '@inertiajs/react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import InputError from '@/theme1/components/input-error';
import PasswordInput from '@/theme1/components/password-input';
import TextLink from '@/theme1/components/text-link';
import { Button } from '@/theme1/components/ui/button';
import { Input } from '@/theme1/components/ui/input';
import { Label } from '@/theme1/components/ui/label';
import { Spinner } from '@/theme1/components/ui/spinner';

type Props = {
    passwordRules: string;
    redirectTo?: string;
};

export default function Register({ passwordRules, redirectTo }: Props) {
    return (
        <>
            <Head title="Register" />

            <section className="relative isolate overflow-hidden bg-[#f6f0e8] py-10 sm:py-14 lg:py-16">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.95),rgba(246,240,232,0.92)_40%,rgba(232,226,214,0.88)_100%)]" />
                <div className="absolute -top-16 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#ead9b4]/35 blur-3xl" />

                <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-white/80 bg-white/90 shadow-[0_30px_90px_rgba(0,0,0,0.12)] backdrop-blur-2xl">
                        <div className="border-b border-neutral-200/70 px-6 py-6 sm:px-8">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.25em] text-neutral-400">Join Captex</p>
                                    <h1 className="mt-3 text-4xl font-black uppercase tracking-[-0.04em] sm:text-5xl">Create account</h1>
                                    <p className="mt-3 max-w-xl text-sm leading-6 text-neutral-500">
                                        Save favourites, track orders, and move through checkout faster.
                                    </p>
                                </div>

                                <Link
                                    href="/login"
                                    className="hidden rounded-full border border-neutral-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-neutral-500 transition hover:border-neutral-900 hover:text-neutral-900 sm:inline-flex"
                                >
                                    Log in
                                </Link>
                            </div>
                        </div>

                        <div className="px-6 py-6 sm:px-8 sm:py-8">
                            <div className="mb-6 flex items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold text-neutral-500">
                                <span className="inline-flex items-center gap-2">
                                    <ShieldCheck className="size-4" />
                                    Create your customer account
                                </span>
                                <span>Frontend layout</span>
                            </div>

                            <Form
                                action="/register"
                                method="post"
                                transform={(data) => ({ ...data, name: `${data.first_name || ''} ${data.last_name || ''}`.trim() })}
                                resetOnSuccess={['password', 'password_confirmation']}
                                disableWhileProcessing
                                className="flex flex-col gap-6"
                            >
                                {({ processing, errors }) => (
                                    <>
                                        {redirectTo && <input type="hidden" name="redirect_to" value={redirectTo} />}
                                        <div className="grid gap-6">
                                            <div className="grid gap-4 sm:grid-cols-2">
                                                <div className="grid gap-2">
                                                    <Label htmlFor="first_name">First name</Label>
                                                    <Input id="first_name" name="first_name" required autoFocus autoComplete="given-name" placeholder="First name" />
                                                </div>
                                                <div className="grid gap-2">
                                                    <Label htmlFor="last_name">Last name</Label>
                                                    <Input id="last_name" name="last_name" required autoComplete="family-name" placeholder="Last name" />
                                                </div>
                                                <InputError message={errors.name} className="sm:col-span-2" />
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="email">Email address</Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    required
                                                    tabIndex={2}
                                                    autoComplete="email"
                                                    name="email"
                                                    placeholder="email@example.com"
                                                />
                                                <InputError message={errors.email} />
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="phone">Phone number</Label>
                                                <Input id="phone" type="tel" name="phone" autoComplete="tel" placeholder="01XXXXXXXXX" />
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="password">Password</Label>
                                                <PasswordInput
                                                    id="password"
                                                    required
                                                    tabIndex={3}
                                                    autoComplete="new-password"
                                                    name="password"
                                                    placeholder="Password"
                                                    passwordrules={passwordRules}
                                                />
                                                <InputError message={errors.password} />
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="password_confirmation">Confirm password</Label>
                                                <PasswordInput
                                                    id="password_confirmation"
                                                    required
                                                    tabIndex={4}
                                                    autoComplete="new-password"
                                                    name="password_confirmation"
                                                    placeholder="Confirm password"
                                                    passwordrules={passwordRules}
                                                />
                                                <InputError message={errors.password_confirmation} />
                                            </div>

                                            <Button
                                                type="submit"
                                                className="mt-2 w-full"
                                                tabIndex={5}
                                                data-test="register-user-button"
                                            >
                                                {processing && <Spinner />}
                                                Create account
                                                <ArrowRight className="size-4" />
                                            </Button>

                                            <label className="flex items-start gap-3 text-sm normal-case! tracking-normal! text-neutral-500">
                                                <input type="checkbox" name="newsletter" className="mt-0.5 size-4 rounded border-neutral-300" />
                                                Send me new drops and members-only releases.
                                            </label>
                                            <label className="flex items-start gap-3 text-sm normal-case! tracking-normal! text-neutral-500">
                                                <input type="checkbox" name="terms" required className="mt-0.5 size-4 rounded border-neutral-300" />
                                                I agree to the terms and privacy policy.
                                            </label>
                                        </div>

                                        <div className="text-center text-sm text-muted-foreground">
                                            Already have an account?{' '}
                                            <TextLink href={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'} tabIndex={6}>
                                                Log in
                                            </TextLink>
                                        </div>
                                    </>
                                )}
                            </Form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
